import { abyssColors } from '@/theme/colors';
import { Stack, Typography } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { LegendColor, LegendCount, LegendLabel, StackedColorBar } from './styles';

type StackedBarChartProps = {
  data: Array<{ name: string; count: number }>;
  displayConfig: Array<{ value: string; color: string }>;
};

const LegendItem = ({ name, count, color }: { name: string; count: number; color?: string }) => {
  return (
    <Stack
      direction="row"
      alignItems="flex-end"
      gap="0.8rem"
      key={`${name}-${count}`}
      sx={{ paddingRight: '15px' }}
    >
      {color && <LegendColor color={color} />}
      <Stack>
        <LegendCount>{count}</LegendCount>
        <LegendLabel>{name}</LegendLabel>
      </Stack>
    </Stack>
  );
};

export const StackedBarChart: React.FC<StackedBarChartProps> = ({ data, displayConfig }) => {
  const totalCount = useMemo(() => {
    return data.reduce((accumulator, currentValue) => accumulator + currentValue.count, 0);
  }, [data]);

  const getCategoryColor = useCallback(
    (categoryName: string) => {
      const config = displayConfig.find((fieldConfig) => fieldConfig.value === categoryName);
      return config ? config.color : '#CCCCCC';
    },
    [displayConfig]
  );

  if (data.length === 0)
    return (
      <Typography
        variant="h6"
        fontWeight={500}
        sx={{
          color: abyssColors.primary[400],
          whiteSpace: 'nowrap',
          mt: 1,
        }}
      >
        No data found
      </Typography>
    );

  return (
    <>
      <Stack direction="row" gap="0.1rem" sx={{ mb: '1.6rem' }}>
        {data.map(({ name, count }, index) => (
          <StackedColorBar
            key={`${name}-${count}`}
            widthPercentage={(count / totalCount) * 100}
            color={getCategoryColor(name)}
            first={index === 0}
            last={index === data?.length - 1}
          />
        ))}
      </Stack>
      <Stack direction="row" gap="2.2rem">
        <LegendItem name={'Total'} count={totalCount} />
        {data.map(({ name: name, count }) => (
          <LegendItem name={name} count={count} color={getCategoryColor(name)} />
        ))}
      </Stack>
    </>
  );
};
